import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { CELLPHONE, EMAIL, PHONE } from "../constants/form";
import { E_REGEX, MOBILE_REGEX_WITH_CODE } from "../utils/regex";

const useYupSchema = () => {
  const { t } = useTranslation("yup");

  const buildBasicSchema = (fields) => {
    const shape = {};

    Object.entries(fields).forEach(([key, value]) => {
      const fieldLabel = value.errorLabel || key;
      const errorMessage = t("requiredField", { field: fieldLabel });

      let validator = yup.string().trim();

      if (value.required) {
        validator = validator.required(errorMessage);
      }

      if (!value.allowEmpty) {
        validator = validator.notOneOf(
          [""],
          t("notEmpty", { field: fieldLabel })
        );
      }

      shape[key] = validator;
    });

    return yup.object().shape(shape);
  };

  const buildDriverSchema = (fields) => {
    const shape = {};

    Object.entries(fields).forEach(([key, value]) => {
      const fieldLabel = value.errorLabel || key;

      let validator = yup.string().trim();

      if (value.required) {
        validator = validator.required(
          t("requiredField", { field: fieldLabel })
        );
      }

      if (!value.allowEmpty) {
        validator = validator.notOneOf(
          [""],
          t("notEmpty", { field: fieldLabel })
        );
      }

      if (key === EMAIL) {
        validator = validator
          .email(t("invalidEmail"))
          .test("is-email", t("invalidEmailFormat"), (value) => {
            if (!value) {
              return true;
            }
            return E_REGEX.test(value);
          });
      }

      if (key === CELLPHONE) {
        validator = validator
          .matches(MOBILE_REGEX_WITH_CODE, t("phoneInvalid"))
          .required(t("phoneRequired"));
      }

      shape[key] = validator;
    });

    return yup.object().shape(shape);
  };

  const buildFleetManagerSchema = (fields) => {
    const shape = {};

    Object.entries(fields).forEach(([key, value]) => {
      let validator = yup.string().trim();
      const fieldLabel = value.errorLabel || key;

      if (value.required) {
        validator = validator.required(
          t("requiredField", { field: fieldLabel })
        );
      }

      if (!value.allowEmpty) {
        validator = validator.notOneOf(
          [""],
          t("notEmpty", { field: fieldLabel })
        );
      }

      if (key === EMAIL) {
        validator = validator
          .email(t("invalidEmail"))
          .test("is-email", t("invalidEmailFormat"), (value) => {
            if (!value) {
              return true;
            }
            return E_REGEX.test(value);
          });
      }

      if (value.type === PHONE) {
        validator = validator
          .matches(MOBILE_REGEX_WITH_CODE, t("phoneInvalid"))
          .required(t("phoneRequired"));
      }

      shape[key] = validator;
    });

    return yup.object().shape(shape);
  };

  const buildAddVehicleSchema = (fields) => {
    const shape = {};

    Object.entries(fields).forEach(([key, value]) => {
      let validator = yup.string().trim().nullable().notRequired();

      const fieldLabel = value.errorLabel;

      if (value.required) {
        validator = validator.test(
          key, // Name of the test
          t("requiredField", { field: fieldLabel }), // Error message
          (val) => !val || val.trim() !== "" // Test function: pass if no value or value is non-empty
        );
      }

      if (value.maxLength) {
        validator = validator.test(
          key,
          t("vinLengthError", { length: value.maxLength }),
          (val) => !val || val.length === value.maxLength // Test function: pass if no value or value length is valid
        );
      }

      shape[key] = validator;
    });

    return yup.object().shape(shape);
  };

  const buildPersonalInfoSchema = (fields) => {
    const shape = {};

    Object.entries(fields).forEach(([key, value]) => {
      let validator = yup.string().trim();
      const fieldLabel = value.errorLabel;

      if (value.required) {
        validator = validator.required(
          t("requiredField", { field: fieldLabel })
        );
      }

      if (!value.allowEmpty) {
        validator = validator.notOneOf(
          [""],
          t("notEmpty", { field: fieldLabel })
        );
      }

      if (value.type === EMAIL) {
        validator = validator
          .email(t("invalidEmail"))
          .test("is-email", t("invalidEmailFormat"), (value) => {
            if (!value) {
              return true;
            }
            return E_REGEX.test(value);
          });
      }

      if (value.type === PHONE) {
        validator = validator
          .matches(MOBILE_REGEX_WITH_CODE, t("phoneInvalid"))
          .required(t("phoneRequired"));
      }

      shape[key] = validator;
    });

    return yup.object().shape(shape);
  };

  const buildVehicleSchema = (fields) => {
    const shape = {};

    Object.entries(fields).forEach(([key, value]) => {
      let validator = yup.string().trim();
      const fieldLabel = value.errorLabel;

      if (value.required) {
        validator = validator.required(
          t("requiredField", { field: fieldLabel })
        );
      }

      shape[key] = validator;
    });

    return yup.object().shape(shape);
  };

  return {
    buildBasicSchema,
    buildDriverSchema,
    buildFleetManagerSchema,
    buildAddVehicleSchema,
    buildPersonalInfoSchema,
    buildVehicleSchema,
  };
};

export default useYupSchema;
